import { useState, useEffect, useRef } from "react";
import { AlertTriangle, CheckCircle, XCircle, Info, X } from "lucide-react";

export type ConfirmVariant = "danger" | "warning" | "success" | "info";

interface ConfirmModalProps {
  open: boolean;
  onConfirm: (note?: string) => void;
  onCancel: () => void;
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  loading?: boolean;
  /** When true, shows a textarea for user input (e.g. rejection reason) */
  withNote?: boolean;
  notePlaceholder?: string;
  noteRequired?: boolean;
}

const VARIANT_CONFIG: Record<
  ConfirmVariant,
  {
    icon: typeof AlertTriangle;
    iconBg: string;
    iconColor: string;
    btnClass: string;
  }
> = {
  danger: {
    icon: AlertTriangle,
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    btnClass: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
  },
  warning: {
    icon: AlertTriangle,
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
    btnClass: "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500",
  },
  success: {
    icon: CheckCircle,
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
    btnClass: "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500",
  },
  info: {
    icon: Info,
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    btnClass: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
  },
};

export default function ConfirmModal({
  open,
  onConfirm,
  onCancel,
  title,
  message,
  confirmLabel = "Konfirmasi",
  cancelLabel = "Batal",
  variant = "danger",
  loading = false,
  withNote = false,
  notePlaceholder = "Tulis catatan...",
  noteRequired = false,
}: ConfirmModalProps) {
  const [note, setNote] = useState("");
  const overlayRef = useRef<HTMLDivElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setNote("");
      // Focus the textarea if shown, otherwise trap focus
      setTimeout(() => noteRef.current?.focus(), 100);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, loading, onCancel]);

  if (!open) return null;

  const config = VARIANT_CONFIG[variant];
  const Icon = config.icon;
  const canConfirm = !withNote || !noteRequired || note.trim().length > 0;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === overlayRef.current && !loading) onCancel();
      }}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" />

      {/* Modal */}
      <div className="relative w-full max-w-md rounded-xl bg-white shadow-xl">
        {/* Close button */}
        <button
          onClick={onCancel}
          disabled={loading}
          className="absolute right-3 top-3 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-6">
          {/* Icon & Title */}
          <div className="flex items-start gap-4">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${config.iconBg}`}
            >
              <Icon className={`h-5 w-5 ${config.iconColor}`} />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">{title}</h3>
              {message && (
                <p className="mt-1.5 text-sm text-gray-500">{message}</p>
              )}
            </div>
          </div>

          {/* Optional note textarea */}
          {withNote && (
            <div className="mt-4">
              <textarea
                ref={noteRef}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={notePlaceholder}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {noteRequired && (
                <p className="mt-1 text-xs text-gray-400">* Wajib diisi</p>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
          {cancelLabel && (
            <button
              onClick={onCancel}
              disabled={loading}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={() => onConfirm(withNote ? note : undefined)}
            disabled={loading || !canConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 ${config.btnClass}`}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Memproses…
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
